import { useEffect, useState } from 'react';
import { api } from '@/api/client';

/** What the server accepts: nginx's default 1 MB request limit, with room. */
const UPLOAD_MAX_BYTES = 900 * 1024;
/** Longest side after shrinking. Sharp on a dashboard, small on the wire. */
const MAX_SIDE_PX = 1600;
/** Beyond this the browser may struggle to even open the file. */
export const PICK_MAX_BYTES = 25 * 1024 * 1024;
export const PICTURE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Turns whatever photo was picked (often several MB straight off a phone) into
 * a JPEG under the upload limit: turned upright, at most 1600px on its longest
 * side, and with the camera's details (location included) left behind,
 * because only the drawn pixels are saved.
 */
export async function shrinkPicture(file: File): Promise<Blob> {
  const source = await decode(file);
  let side = MAX_SIDE_PX;

  for (let attempt = 0; attempt < 4; attempt++) {
    const scale = Math.min(1, side / Math.max(source.width, source.height));
    const width = Math.max(1, Math.round(source.width * scale));
    const height = Math.max(1, Math.round(source.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser cannot prepare pictures.');
    // A transparent PNG would turn black as a JPEG; paper-white reads right.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(source.image, 0, 0, width, height);

    for (const quality of [0.85, 0.75, 0.65]) {
      const blob = await toJpeg(canvas, quality);
      if (blob.size <= UPLOAD_MAX_BYTES) return blob;
    }
    side = Math.round(side * 0.75);
  }
  throw new Error('This picture could not be made small enough. Try another one.');
}

async function decode(
  file: File,
): Promise<{ image: CanvasImageSource; width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { image: bitmap, width: bitmap.width, height: bitmap.height };
    } catch {
      // Fall through to <img>, which some browsers decode more formats with.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return { image: img, width: img.naturalWidth, height: img.naturalHeight };
  } catch {
    throw new Error('This file could not be opened as a picture.');
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The picture could not be saved.'))),
      'image/jpeg',
      quality,
    ),
  );
}

/**
 * One fetch per picture version for the whole session, shared by the ticker,
 * the card and the pop-up. Keyed on the image key, which changes whenever the
 * picture does, so a replaced picture is fetched afresh. The server forbids
 * the browser from storing these (a farm PC is shared), so this memory copy
 * is the only one, and it goes with the tab.
 */
const loaded = new Map<string, Promise<string>>();

function loadPicture(id: string, imageKey: string): Promise<string> {
  const key = `${id}:${imageKey}`;
  let pending = loaded.get(key);
  if (!pending) {
    pending = api
      .blob(`/api/v1/announcements/${id}/image`)
      .then((blob) => URL.createObjectURL(blob));
    // A failure is not remembered, so the next render can try again.
    pending.catch(() => loaded.delete(key));
    loaded.set(key, pending);
  }
  return pending;
}

export function useAnnouncementPicture(
  id: string,
  imageKey: string | null | undefined,
): { url: string | null; failed: boolean } {
  const [state, setState] = useState<{ key: string; url: string | null; failed: boolean }>({
    key: '',
    url: null,
    failed: false,
  });
  const key = imageKey ? `${id}:${imageKey}` : '';

  useEffect(() => {
    if (!imageKey) return;
    let live = true;
    loadPicture(id, imageKey).then(
      (url) => live && setState({ key: `${id}:${imageKey}`, url, failed: false }),
      () => live && setState({ key: `${id}:${imageKey}`, url: null, failed: true }),
    );
    return () => {
      live = false;
    };
  }, [id, imageKey]);

  // Ignore a result that belongs to a picture this one has since replaced.
  return state.key === key ? { url: state.url, failed: state.failed } : { url: null, failed: false };
}

/**
 * An announcement's picture. Holds its space while loading so the card does
 * not jump, and says so plainly if it could not be loaded.
 */
export function AnnouncementPicture({
  id,
  imageKey,
  alt,
  className = '',
  onOpen,
  src,
}: {
  id: string;
  imageKey: string | null | undefined;
  alt: string;
  className?: string;
  /** Makes the picture a button, for opening it larger. */
  onOpen?: (() => void) | undefined;
  /** Shows this instead of fetching: a picture just picked, not yet saved. */
  src?: string | undefined;
}) {
  const fetched = useAnnouncementPicture(id, src ? null : imageKey);
  const { url, failed } = src ? { url: src, failed: false } : fetched;
  if (!imageKey && !src) return null;

  if (failed) {
    return <p className={`announcementpicture announcementpicture--failed ${className}`}>The picture could not be loaded.</p>;
  }
  if (!url) {
    return <div className={`announcementpicture announcementpicture--loading ${className}`} aria-label="Loading picture" />;
  }

  const img = <img src={url} alt={alt} className="announcementpicture__img" />;
  return onOpen ? (
    <button
      type="button"
      className={`announcementpicture announcementpicture--button ${className}`}
      onClick={onOpen}
      aria-label={`Open the picture larger: ${alt}`}
    >
      {img}
    </button>
  ) : (
    <div className={`announcementpicture ${className}`}>{img}</div>
  );
}
